import { useGetAiSlideContentQuery } from "@api/slides-library";
import { SelectionOrderBadge } from "@modules/slides-library/shared/components/selection-order-badge";
import type { StaticEntityType } from "@config";
import useAddSlides from "@editor/hooks/use-add-slides";
import { useEnsureReplaceNewSlide } from "@editor/hooks/use-ensure-replace-new-slide";
import { cn } from "@lib/cn";
import {
  getRecentlyUsedCategory,
  SlidePreview,
  useRecentlyUsedStore,
  useSlidesLibraryStore,
  useSlidesLibraryStoreShallow,
} from "@modules/slides-library/shared";
import Checkbox from "@components/ui/checkbox";
import { useAppDispatch, useAppSelector } from "@store";
import { isPlan } from "@store/account/selectors";
import { openProDialog } from "@/shared";
import { closeDialog, openDialog } from "@store/dialogs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { StorydocSparklesIcon } from "@components/ui/icons/sparkles";
import { IconDots, IconPlus, IconStar } from "@tabler/icons-react";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useIntersectionObserver } from "usehooks-ts";
import { track } from "@/features/analytics";
import { FEATURE_FLAGS, useFeatureFlag } from "@/lib/feature-flags";
import type { TDesignSlide } from "@/shared/types";
import { SlideSaveDialog } from "../../saved-slides";
import { setSlideDefaultValues } from "../utils";
import { GenerateWithAiCustomPrompt } from "./generate-with-ai-custom-prompt";
import { produce } from "immer";

const ProBadgeIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="Pro"
  >
    <rect
      x="0.5"
      y="0.5"
      width="15"
      height="15"
      rx="3.5"
      fill="#F97316"
      stroke="#EA580C"
    />
    <path
      d="M4.39062 10.5276H11.6129V11.2498H4.39062V10.5276ZM4.39062 5.47201L6.19618 6.55534L8.00174 4.38867L9.8073 6.55534L11.6129 5.47201V9.80534H4.39062V5.47201Z"
      fill="white"
    />
  </svg>
);

const UpgradeCrownIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M3.83496 10.9168H12.1683V11.7502H3.83496V10.9168ZM3.83496 5.0835L5.91829 6.3335L8.00164 3.8335L10.085 6.3335L12.1683 5.0835V10.0835H3.83496V5.0835Z"
      fill="white"
    />
  </svg>
);

export const SingleSlidePreview = memo(function SingleSlidePreview({
  slide,
  showCategoryBadge = false,
}: {
  slide: TDesignSlide;
  showCategoryBadge?: boolean;
}) {
  // React Compiler opt-out: this component subscribes to several Zustand
  // selectors (selection, multi-select mode, type, view) whose return values
  // are unstable across renders; the compiler's auto-memoization produces
  // incorrect re-renders for the thumbnail grid.
  "use no memo";
  const dispatch = useAppDispatch();
  const { handleAddSlides } = useAddSlides();
  const ensureReplaceNewSlide = useEnsureReplaceNewSlide();
  const { ref, isIntersecting } = useIntersectionObserver({
    threshold: 0.3,
  });
  const {
    view,
    handleMultiselectSlide,
    isSelected,
    isInMultiSelectMode,
    type,
  } = useSlidesLibraryStoreShallow((s) => ({
    view: s.view,
    isSelected: s.isSelected(slide._id),
    handleMultiselectSlide: s.handleMultiselectSlide,
    isInMultiSelectMode: s.isInMultiSelectMode(isIntersecting),
    type: s.type,
  }));
  const [shouldRender, setShouldRender] = useState(false);
  const [isAiOpen, setIsAiOpen] = useState(false);
  // "Add with AI" is AI on slides, rolled out per org behind its flag.
  const aiSlidesOn = useFeatureFlag(FEATURE_FLAGS.AI_SLIDES);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // JSON-sourced layouts ship their content inline; only hit the content API
  // for slides without it (e.g. backend templates).
  const { data } = useGetAiSlideContentQuery(
    { id: slide._id },
    { skip: !isIntersecting || !!slide.content },
  );
  const json = slide.content ?? (data?.json as StaticEntityType | undefined);

  // Pro slides are gated for users without a Pro/Team plan.
  const isProPlan = useAppSelector(isPlan("pro"));
  const isTeamPlan = useAppSelector(isPlan("team"));
  const isProLocked = !!slide.isPro && !isProPlan && !isTeamPlan;

  const parsedSlide = useMemo(() => {
    if (!json) return null;
    return setSlideDefaultValues({
      slide: json,
      defaultValues: slide.defaultData || {},
    });
  }, [slide.defaultData, json]);

  useEffect(() => {
    if (isIntersecting && json) setShouldRender(true);
    else {
      const timeout = setTimeout(() => setShouldRender(false), 200);
      return () => clearTimeout(timeout);
    }
  }, [isIntersecting, json]);

  // This preview renders inside any of these host dialogs — close them all
  // so nothing stays stacked behind whatever we open next.
  const closeHostDialogs = useCallback(() => {
    dispatch(closeDialog("slides-library"));
    dispatch(closeDialog("saved-slides-dialog"));
    dispatch(closeDialog("layouts-dialog"));
    dispatch(closeDialog("templates-dialog"));
  }, [dispatch]);

  const handleAddAction = useCallback(
    (s: StaticEntityType) => {
      if (view === "editor") {
        const { templateTab } = useSlidesLibraryStore.getState();
        const category = getRecentlyUsedCategory(type, templateTab);
        if (category === "layouts" || category === "templates") {
          useRecentlyUsedStore.getState().recordUse(category, slide._id);
        }
        // Replace a just-added blank slide in place (mirrors the inline
        // layouts strip) instead of relying on the lingering ai-slides
        // placement, which the library dialog resets on unmount.
        ensureReplaceNewSlide();
        handleAddSlides([s]);
        closeHostDialogs();
        return;
      }
      handleMultiselectSlide(
        { id: slide._id, slide: s, type: "template" },
        isSelected ? "remove" : "add",
      );
    },
    [
      view,
      type,
      slide._id,
      isSelected,
      handleAddSlides,
      ensureReplaceNewSlide,
      handleMultiselectSlide,
      closeHostDialogs,
    ],
  );

  const handleAddSlide = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isProLocked) {
        // Same behavior as the "Upgrade now" overlay button: close the host
        // dialog so the upgrade modal isn't stacked behind it.
        closeHostDialogs();
        return openProDialog("pro-slide");
      }
      if (!parsedSlide) return;
      const updated = produce(parsedSlide, (draft) => {
        draft.settings.notionMasterSlideId = slide.masterSlide;
        draft.settings.notionDesignSlideId = slide._id;
      });
      handleAddAction(updated);
    },
    [slide, parsedSlide, handleAddAction, isProLocked, closeHostDialogs],
  );

  const handleClick = useCallback(() => {
    if (!parsedSlide || isProLocked) return;
    // The library view has no per-slide action of its own — picking slides
    // there builds the multi-select that turns into a new story — so the whole
    // card toggles selection, the same way a saved slide does.
    if (isInMultiSelectMode || view === "library") {
      handleMultiselectSlide(
        { id: slide._id, slide: parsedSlide, type: "template" },
        isSelected ? "remove" : "add",
      );
    }
  }, [
    isInMultiSelectMode,
    view,
    slide._id,
    parsedSlide,
    isSelected,
    handleMultiselectSlide,
    isProLocked,
  ]);

  const handleSaveToMySlides = useCallback(() => {
    track("save slide", { from: type });
    dispatch(
      openDialog({
        id: "save-to-my-slides",
        content: SlideSaveDialog,
        props: { slide: parsedSlide, title: slide.title },
      }),
    );
  }, [dispatch, parsedSlide, slide.title, type]);

  return (
    <div
      ref={ref}
      data-menu-open={isMenuOpen || isSelected}
      className={cn(
        "tw-group/slide tw-relative tw-flex tw-w-[234px] tw-flex-col tw-gap-2 tw-rounded-xl tw-bg-white tw-p-2 tw-transition-colors hover:tw-bg-blue-50",
        // Match the badge's own render condition so cards without a category
        // (e.g. backend template slides) don't get the taller frame + empty gap.
        showCategoryBadge && slide.categoryTitle
          ? "tw-h-[200px]"
          : "tw-h-[170px]",
        (isMenuOpen || isSelected || isAiOpen) && "tw-bg-blue-50",
      )}
    >
      <div className="tw-relative tw-cursor-pointer" onClick={handleClick}>
        {/* Hover overlay with buttons -- THE EDITOR'S, not the platform's.
            Out in the library a card is there to be chosen, and neither
            button has anywhere to put a slide; the scrim would only wash it
            out and swallow the click that selects it. A pro-locked slide is
            the exception: there the overlay is the upgrade path, and the
            click that would select it is refused anyway. */}
        {!isInMultiSelectMode && (view === "editor" || isProLocked) && (
          <div
            className={cn(
              "tw-invisible tw-absolute tw-inset-0 tw-z-2 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-bg-white/95 tw-opacity-0 tw-shadow-sm tw-outline tw-outline-1 !tw-outline-offset-0 tw-outline-blue-200 tw-transition-[opacity,visibility] group-hover/slide:tw-visible group-hover/slide:tw-opacity-100",
              isAiOpen && "tw-visible tw-opacity-100",
            )}
          >
            {isProLocked ? (
              <button
                type="button"
                onClick={handleAddSlide}
                aria-label={`Upgrade to add: ${slide.title}`}
                className="tw-flex tw-h-9 tw-w-[129px] tw-cursor-pointer tw-items-center tw-justify-center tw-gap-1 tw-rounded-lg tw-border-0 tw-bg-pro-base tw-font-sans tw-text-sm tw-font-medium tw-text-white tw-shadow-2xs hover:tw-opacity-90"
              >
                <UpgradeCrownIcon />
                Upgrade now
              </button>
            ) : (
              <>
                {view === "editor" && (
                  <button
                    type="button"
                    onClick={handleAddSlide}
                    aria-label={`Add slide: ${slide.title}`}
                    className="tw-flex tw-h-9 tw-w-[129px] tw-cursor-pointer tw-items-center tw-justify-center tw-gap-1 tw-rounded-lg tw-bg-white tw-font-sans tw-text-sm tw-font-medium tw-text-blue tw-shadow-2xs tw-outline tw-outline-1 !tw-outline-offset-0 tw-outline-blue-100 hover:tw-bg-blue-50"
                  >
                    <IconPlus
                      size={16}
                      stroke={1.5}
                      className="tw-fill-none tw-text-blue"
                      aria-hidden="true"
                    />
                    Add slide
                  </button>
                )}
                {/* NOT BEFORE THE LAYOUT TREE IS HERE. A template without
                    inline content has no `json` until its content query
                    settles, and the button was live the whole time -- pressed
                    then, it submitted slideData: null and the generator had no
                    tree to bind, so the slide came back empty. */}
                {aiSlidesOn && json && (
                  <GenerateWithAiCustomPrompt
                    isOpen={isAiOpen}
                    setIsOpen={setIsAiOpen}
                    design={slide}
                    slide={json}
                    defaultValues={slide.defaultData || {}}
                  >
                    <button
                      type="button"
                      aria-label={`Add slide with AI: ${slide.title}`}
                      aria-expanded={isAiOpen}
                      aria-haspopup="dialog"
                      className={cn(
                        "tw-flex tw-h-9 tw-w-[129px] tw-cursor-pointer tw-items-center tw-justify-center tw-gap-1 tw-rounded-lg tw-font-sans tw-text-sm tw-font-medium tw-text-primary tw-shadow-2xs tw-outline tw-outline-1 !tw-outline-offset-0 tw-outline-blue-100 hover:tw-bg-violet-50",
                        isAiOpen ? "tw-bg-violet-50" : "tw-bg-white",
                      )}
                    >
                      <StorydocSparklesIcon
                        size={16}
                        className="tw-text-primary"
                        aria-hidden="true"
                      />
                      Add with AI
                    </button>
                  </GenerateWithAiCustomPrompt>
                )}
              </>
            )}
          </div>
        )}
        {isProLocked && (
          <div className="tw-absolute tw-left-2 tw-top-2 tw-z-[3] tw-flex">
            <ProBadgeIcon />
          </div>
        )}
        <SlidePreview
          slide={parsedSlide}
          isIntersecting={isIntersecting}
          shouldRender={shouldRender}
        />
      </div>
      <div className="tw-flex tw-h-6 tw-w-[218px] tw-items-center tw-gap-2">
        <Checkbox
          checked={isSelected}
          disabled={isProLocked}
          onCheckedChange={() =>
            handleMultiselectSlide(
              {
                id: slide._id,
                slide: parsedSlide as StaticEntityType,
                type: "template",
              },
              isSelected ? "remove" : "add",
            )
          }
          className={cn(
            "tw-hidden tw-shrink-0 group-hover/slide:tw-flex",
            (isInMultiSelectMode || isSelected || isMenuOpen) && "tw-flex",
          )}
        >
          <Checkbox.Toggle className="tw-size-4 tw-rounded !tw-border-0 tw-shadow-xs tw-outline tw-outline-1 !tw-outline-offset-0 tw-outline-blue/5 [&_i]:tw-size-3 [&_i]:tw-text-xs/none" />
        </Checkbox>
        <span className="tw-block tw-min-w-0 tw-flex-1 tw-truncate tw-font-sans tw-text-sm tw-font-normal tw-leading-5 tw-text-blue">
          {slide.title}
        </span>
        <SelectionOrderBadge id={slide._id} />
        {!isInMultiSelectMode && (
          <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`Open actions menu for ${slide.title}`}
                aria-haspopup="menu"
                aria-expanded={isMenuOpen}
                className={cn(
                  "tw-bg-transparent tw-invisible tw-flex tw-h-6 tw-w-6 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-md tw-border-none tw-p-0 tw-opacity-0 tw-outline-none tw-transition-[opacity,background-color] hover:tw-bg-blue-100 group-hover/slide:tw-visible group-hover/slide:tw-opacity-100",
                  isMenuOpen && "!tw-visible !tw-bg-blue-100 !tw-opacity-100",
                )}
              >
                <IconDots
                  className="tw-size-3.5 tw-text-blue tw-opacity-50"
                  stroke={1}
                  aria-hidden="true"
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="tw-w-40 tw-p-1">
              <DropdownMenuItem
                className="tw-h-8 tw-gap-2 tw-rounded tw-px-2 tw-py-1.5 tw-text-[14px] tw-text-blue data-[highlighted]:tw-bg-blue-50"
                onClick={handleSaveToMySlides}
              >
                <IconStar
                  className="tw-size-4 tw-shrink-0 tw-text-blue"
                  stroke={1}
                />
                Save to my slides
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {showCategoryBadge && slide.categoryTitle && (
        <span className="-tw-mt-1 tw-font-sans tw-text-xs tw-font-normal tw-text-blue-500">
          {slide.categoryTitle}
        </span>
      )}
    </div>
  );
});
